import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { API_GET_FIELDS_URI, API_GET_MODELS_URI } from "./Constants";
import { useAlertContext } from "./AlertContext";
import { useEngineConfigContext } from "./EngineConfigContext";
import { fieldOperatorsByType } from "./FieldProperties";
import { getPersistedWizardContextState } from "../utils/apiGeneratorPersistence";

const persistedWizard = getPersistedWizardContextState();

const ApiGenerationContext = createContext({
  submitting: false,
  fetching: false,
  models: [],
  modelRecords: [],
  fields: {},
  currentModel: null,
  selectedRecord: null,
  currentRecord: null,
  setCurrentModel: async () => {},
  setSelectedRecord: async () => {},
  fetchModels: async () => {},
  fetchModelRecords: async () => {},
  fetchRecords: async () => {},
  fetchAllRecords: async () => {},
  fetchFields: async () => {},
  fetchRecordById: async () => {},
  selectedFields: [],
  setSelectedFields: () => {},
  QBFields: [],
  setQBFields: () => {},
  filterFields: [],
  setFilterFields: () => {},
  requiredFields: [],
  setRequiredFields: () => {},
  fieldType: [],
  setFieldType: () => {},
  selectedMethod: "",
  setSelectedMethod: () => {},
  selectedConfig: null,
  setSelectedConfig: () => {},
  prevModel: null,
  setPrevModel: () => {},
  selectedVersion: null,
  setSelectedVersion: () => {},
  prevConfig: null,
  setPrevConfig: () => {},
  prevVersion: null,
  setPrevVersion: () => {},
  odooEngineVersion: null,
  setOdooEngineVersion: () => {},
  setModelRecords: () => {}
});

const ApiGenerationContextProvider = ({ children }) => {
  const { setCurrentConfig, currentConfig, list } = useEngineConfigContext();
  //  const [currentVersion, setCurrentVersion] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [currentModel, setCurrentModel] = useState(persistedWizard?.currentModel ?? null);
  const [selectedConfig, setSelectedConfig] = useState(persistedWizard?.selectedConfig ?? null);
  const [models, setModels] = useState(persistedWizard?.models ?? []);
  const [selectedVersion, setSelectedVersion] = useState(persistedWizard?.selectedVersion ?? null);
  const [modelRecords, setModelRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(persistedWizard?.selectedRecord ?? null);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [fields, setFields] = useState({});
  const [selectedFields, setSelectedFields] = useState(persistedWizard?.selectedFields ?? []);
  const [completeQuery, setCompleteQuery] = useState(persistedWizard?.completeQuery ?? []);
  const [QBFields, setQBFields] = useState(persistedWizard?.QBFields ?? []);
  const [filterFields, setFilterFields] = useState(persistedWizard?.filterFields ?? []);
  const [requiredFields, setRequiredFields] = useState(persistedWizard?.requiredFields ?? []);
  const [fieldType, setFieldType] = useState(persistedWizard?.fieldType ?? {});
  const [selectedMethod, setSelectedMethod] = useState(persistedWizard?.selectedMethod ?? "");
  const [prevModel, setPrevModel] = useState(null);
  const [prevConfig, setPrevConfig] = useState(null);
  const [prevVersion, setPrevVersion] = useState(null);
  const [odooEngineVersion, setOdooEngineVersion] = useState(null);
  const { setError } = useAlertContext();

  useEffect(() => {
    console.log("Config list", list, currentConfig);
    var engineVersion;
    list.map((item) => {
      engineVersion = process.env.REACT_APP_PREFIX + item.version;
    });
    console.log("Engine version", engineVersion);
    setCurrentConfig(list.find(({ name }) => name === selectedConfig));
  }, [selectedConfig, list]);

  useEffect(() => {
    let filteredFields = Object.entries(fields).filter(([, f]) => f.required);
    if (selectedFields.length === 0) {
      setSelectedFields(filteredFields.map(([name]) => name));
      setRequiredFields(filteredFields.map(([name]) => name));
      // "x_address": {"type": "string"}
      filteredFields.map(([name, f]) => (fieldType[name] = { type: f.type }));
      filteredFields.map(([name, f]) => {
        let ops = fieldOperatorsByType[f.type]
          ? { operators: fieldOperatorsByType[f.type].operators }
          : {};
        QBFields.push({
          ...ops,
          name: name,
          label: f.string,
          type: f.type,
          inputType: f.type == "boolean" ? "checkbox" : f.type == "integer" ? "number" : f.type,
          valueEditorType:
            f.type == "boolean" ? "checkbox" : f.type == "integer" ? "number" : f.type
        });
      });
      setFieldType(fieldType);
      console.log(QBFields);
      setQBFields(QBFields);
    }
  }, [fields]);

  const fetchModels = useCallback(async () => {
    if (selectedConfig && (selectedConfig !== prevConfig || models.length === 0)) {
      setFetching(true);
      if (models.length === 0) {
        setModels([]);
      }
      var engineVersion;
      list.map((item) => {
        engineVersion = process.env.REACT_APP_PREFIX + item.version;
      });
      setOdooEngineVersion(engineVersion);
      try {
        const modelsResp = await axios
          .post(API_GET_MODELS_URI, {
            configName: selectedConfig,
            Engine_version: engineVersion,
            model: "ir.model",
            fields: [["id", ">", 0]],
            outputrecords: { fields: ["display_name", "model"], limit: 1000 }
          })
          .then((resp) => resp.data);
        setModels(modelsResp || []);
        setPrevConfig(selectedConfig);
        setPrevVersion(selectedVersion);
      } catch (e) {
        setError(e.message);
        throw new Error(e.message);
      } finally {
        setFetching(false);
      }
    }
  }, [selectedConfig, prevConfig, models.length, list, selectedVersion, setError]);

  const fetchFields = useCallback(async () => {
    if (
      selectedConfig &&
      currentModel &&
      (currentModel !== prevModel ||
        selectedConfig !== prevConfig ||
        Object.keys(fields).length === 0)
    ) {
      console.log("fetching fields");
      if (Object.keys(fields).length === 0) {
        setFields({});
      }
      setFetching(true);
      try {
        const resp = await axios.post(API_GET_FIELDS_URI, {
          configName: selectedConfig,
          model: currentModel
        });
        setFields(resp.data || {});
        setPrevModel(currentModel);
        setPrevConfig(selectedConfig);
      } catch (e) {
        setError(e.message);
        throw new Error(e.message);
      } finally {
        setFetching(false);
      }
    }
  }, [selectedConfig, currentModel, prevModel, prevConfig, fields, setError]);

  const fetchModelRecords = async (model, fields) => {
    setModelRecords([]);
    setFetching(true);
    try {
      setModelRecords(await fetchRecords(model, fields));
    } catch (e) {
      setError(e.message);
      throw new Error(e.message);
    } finally {
      setFetching(false);
    }
  };

  const fetchRecords = useCallback(
    async (model, fields = ["display_name", "id"]) => {
      if (selectedConfig) {
        return await axios
          .post(API_GET_MODELS_URI, {
            configName: selectedConfig,
            model,
            fields: [["id", ">", 0]],
            input: { fields },
            outputrecords: { fields, limit: 1000 }
          })
          .then((resp) => resp.data);
      }
      throw new Error("Engine Configuration is not selected!");
    },
    [selectedConfig]
  );

  const fetchAllRecords = useCallback(
    async (model, selectedFields) => {
      console.log(model);
      console.log(completeQuery);
      console.log("Selectedfields>>", selectedFields);
      selectedFields.indexOf("display_name") < 0 ? selectedFields.push("display_name") : null;
      //let outputRecordsFields = model === "res.partner" ? "name" : " ";
      var engineVersion;
      list.map((item) => {
        engineVersion = process.env.REACT_APP_PREFIX + item.version;
      });
      setOdooEngineVersion(engineVersion);
      if (selectedConfig) {
        try {
          setFetching(true);
          return await axios
            .post(API_GET_MODELS_URI, {
              configName: selectedConfig,
              Engine_version: engineVersion,
              model,
              fields: completeQuery ? completeQuery : [["id", ">", 0]],
              outputrecords: {
                fields: selectedFields,
                limit: 1000
              }
            })
            .then((resp) => resp.data);
        } catch (e) {
          setFetching(false);
          throw new Error("Engine Configuration is not selected!");
        } finally {
          setFetching(false);
        }
      }
    },
    [selectedConfig]
  );

  const fetchRecordById = useCallback(
    async (model, id) => {
      if (currentConfig) {
        var engineVersion;
        list.map((item) => {
          engineVersion = process.env.REACT_APP_PREFIX + item.version;
        });
        setOdooEngineVersion(engineVersion);
        try {
          setCurrentRecord(null);
          setFetching(true);
          const record = await axios
            .post(API_GET_MODELS_URI, {
              configName: currentConfig.name,
              Engine_version: engineVersion,
              model,
              fields: [["id", "=", id]],
              input: { limit: 1 },
              outputrecords: {}
            })
            .then((resp) => resp.data)
            .then((records) => records?.shift())
            .finally(() => {
              setFetching(false);
            });
          setCurrentRecord(record);
        } catch (e) {
          setError(e.message);
          throw new Error(e.message);
        } finally {
          setFetching(false);
        }
      }
    },
    [currentConfig]
  );

  return (
    <ApiGenerationContext.Provider
      value={{
        currentModel,
        fetching,
        models,
        fields,
        modelRecords,
        selectedRecord,
        currentRecord,
        fetchRecordById,
        fetchModels,
        fetchFields,
        setCurrentModel,
        fetchModelRecords,
        fetchRecords,
        fetchAllRecords,
        setSelectedRecord,
        selectedFields,
        setSelectedFields,
        completeQuery,
        setCompleteQuery,
        filterFields,
        setFilterFields,
        requiredFields,
        setRequiredFields,
        fieldType,
        setFieldType,
        QBFields,
        setQBFields,
        selectedMethod,
        setSelectedMethod,
        selectedConfig,
        setSelectedConfig,
        selectedVersion,
        setSelectedVersion,
        prevModel,
        setPrevModel,
        prevConfig,
        setPrevConfig,
        prevVersion,
        setPrevVersion,
        setModelRecords,
        odooEngineVersion,
        setOdooEngineVersion
      }}>
      {children}
    </ApiGenerationContext.Provider>
  );
};

ApiGenerationContextProvider.propTypes = {
  children: PropTypes.element.isRequired
};

const useApiGenerationContext = () => useContext(ApiGenerationContext);

export { ApiGenerationContextProvider, ApiGenerationContext, useApiGenerationContext };
